import React, { useMemo } from 'react';
import { useQueryParam, useIsActiveRoute } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import/no-restricted-import
import { SmartCardProvider } from '@atlaskit/link-provider/provider';
import type { ProductType } from '@atlaskit/linking-common/types';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useGlobalObjectPreviewPanels } from '@jira/issue__preview-panels/src/useGlobalObjectPreviewPanels.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { ProjectContext } from '@jira/router-resources__project-context/src/ProjectContext.types.tsx';
import { useRovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { getCorrectContentURL } from '@atlassian/confluence-page-preview-panel/utils/custom-domains';

import { isWorkItemModernizationGatesEnabled } from '@jira/issue__eligibility/src/isWorkItemModernizationGatesEnabled.tsx';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { SyncJiraSnippetRenderer } from './SyncJiraSnippetRenderer.tsx';

// A Jira work item smart card resolves to an ARI of the form
// `ari:cloud:jira:{cloudId}:issue/{id}` (resourceOwner `jira`, resourceType `issue`).
const JIRA_ISSUE_ARI_REGEX = /^ari:cloud:jira:[^:]*:issue\//;
const BROWSE_URL_ISSUE_KEY_REGEX = /\/browse\/([A-Za-z][A-Za-z0-9_]*-\d+)/;

const getJiraIssueKeyFromSmartCard = (ari: string, url: string): string | undefined => {
	if (!JIRA_ISSUE_ARI_REGEX.test(ari)) {
		return undefined;
	}

	try {
		const match = new URL(url).pathname.match(BROWSE_URL_ISSUE_KEY_REGEX);
		return match?.[1];
	} catch {
		return undefined;
	}
};

const isNativePreviewPanelOnWorkItemViewEnabled = (): boolean =>
	[
		fg('migrate-iframe-to-native-preview-on-wiv'),
		fg('jira-glance-panel'),
		fg('jira_preview_panel_m_1_rollout'),
	].every(Boolean);

const LinkingProvidersOld = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	const { data } = useProjectContext();
	const cloudId = useCloudId();

	// Short term: Fire jira_aa_test_fy27h1 exposure for Jira FY27H1 Holdout
	// This is needed due to crossover units exposed by confluence_web affecting jira_web
	// see: https://atlassian.slack.com/archives/C04PR2YE4UC/p1781650989840319?thread_ts=1781401144.742829&cid=C04PR2YE4UC
	// do not remove - ping #twc-holdout-working-group to discuss
	if (data?.projectType === 'software' || data?.projectType === 'business') {
		expVal('jira_aa_test_fy27h1', 'isEnabled', false);
	}

	const product = getProductForSmartCardProvider(data);

	const atlassianIntelligenceProps = useMemo(() => {
		if (!fg('jira-cc-ai-snippet-gate') || !data?.isAIEnabled) {
			return {};
		}

		return {
			renderers: {
				snippet: SyncJiraSnippetRenderer,
			},
		};
	}, [data?.isAIEnabled]);

	const useRovoEntitlementResourceGated = functionUnionWithCondition(
		() => fg('platform_sl_3p_auth_rovo_entitlement'),
		useRovoEntitlementResource,
		() => undefined,
	);
	const rovoEntitlement = useRovoEntitlementResourceGated();
	const rovoOptions = useMemo(
		() =>
			rovoEntitlement?.data
				? {
						isRovoEnabled: Boolean(rovoEntitlement.data.isRovoEnabled),
						isRovoLLMEnabled: Boolean(rovoEntitlement.data.isRovoLLMEnabled),
					}
				: undefined,
		[rovoEntitlement],
	);

	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('jira-glance-panel') && fg('jira_preview_panel_m_1_rollout')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const smartCardProviderProps = useGlobalObjectPreviewPanels();
		const open = smartCardProviderProps.openPreviewPanel;

		// Create a wrapper function that updates the preview panel URL
		// This is used for custom domains to update to the correct content URL and avoid CSP issues

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const openPreviewPanelWithModifiedURL = useMemo(
			() => (props: Parameters<(typeof smartCardProviderProps)['openPreviewPanel']>[0]) =>
				open({
					...props,
					url: getCorrectContentURL(cloudId, props.url, props.ari),
					ari: props.ari,
				}),
			[open, cloudId],
		);

		return (
			<SmartCardProvider
				{...smartCardProviderProps}
				openPreviewPanel={openPreviewPanelWithModifiedURL}
				product={product}
				isAdminHubAIEnabled={data?.isAIEnabled ?? false}
				rovoOptions={rovoOptions}
				{...atlassianIntelligenceProps}
				xpcProduct="jira"
				xpcSubProduct={product}
			>
				{children}
			</SmartCardProvider>
		);
	}

	return (
		<SmartCardProvider
			product={product}
			isAdminHubAIEnabled={data?.isAIEnabled ?? false}
			rovoOptions={rovoOptions}
			{...atlassianIntelligenceProps}
		>
			{children}
		</SmartCardProvider>
	);
};

const LinkingProvidersNew = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	const { data } = useProjectContext();
	const cloudId = useCloudId();

	// Short term: Fire jira_aa_test_fy27h1 exposure for Jira FY27H1 Holdout
	// This is needed due to crossover units exposed by confluence_web affecting jira_web
	// see: https://atlassian.slack.com/archives/C04PR2YE4UC/p1781650989840319?thread_ts=1781401144.742829&cid=C04PR2YE4UC
	// do not remove - ping #twc-holdout-working-group to discuss
	if (data?.projectType === 'software' || data?.projectType === 'business') {
		expVal('jira_aa_test_fy27h1', 'isEnabled', false);
	}

	const product = getProductForSmartCardProvider(data);

	const atlassianIntelligenceProps = useMemo(() => {
		if (!fg('jira-cc-ai-snippet-gate') || !data?.isAIEnabled) {
			return {};
		}

		return {
			renderers: {
				snippet: SyncJiraSnippetRenderer,
			},
		};
	}, [data?.isAIEnabled]);

	const useRovoEntitlementResourceGated = functionUnionWithCondition(
		() => fg('platform_sl_3p_auth_rovo_entitlement'),
		useRovoEntitlementResource,
		() => undefined,
	);
	const rovoEntitlement = useRovoEntitlementResourceGated();
	const rovoOptions = useMemo(
		() =>
			rovoEntitlement?.data
				? {
						isRovoEnabled: Boolean(rovoEntitlement.data.isRovoEnabled),
						isRovoLLMEnabled: Boolean(rovoEntitlement.data.isRovoLLMEnabled),
					}
				: undefined,
		[rovoEntitlement],
	);

	const smartCardProviderProps = useGlobalObjectPreviewPanels();

	const isWorkItemRoute = useIsActiveRoute('work-item');

	const [, setSelectedIssue] = useQueryParam('selectedIssue');

	const open = smartCardProviderProps.openPreviewPanel;

	const isFullScreenNewWorkItemView = isWorkItemRoute && isWorkItemModernizationGatesEnabled();
	const isOpeningNativePreviewPanelForJiraSmartCard =
		isFullScreenNewWorkItemView && fg('opening_native_preview_panel_for_jira_smart_card');

	// Create a wrapper function that updates the preview panel URL
	// This is used for custom domains to update to the correct content URL and avoid CSP issues
	const openPreviewPanelWithModifiedURL = useMemo(
		() => (props: Parameters<(typeof smartCardProviderProps)['openPreviewPanel']>[0]) => {
			if (isOpeningNativePreviewPanelForJiraSmartCard) {
				const issueKey = getJiraIssueKeyFromSmartCard(props.ari, props.url);
				if (issueKey) {
					setSelectedIssue(issueKey);
					return;
				}
			}

			open({
				...props,
				url: getCorrectContentURL(cloudId, props.url, props.ari),
				ari: props.ari,
			});
		},
		[isOpeningNativePreviewPanelForJiraSmartCard, open, cloudId, setSelectedIssue],
	);

	return (
		<SmartCardProvider
			{...smartCardProviderProps}
			openPreviewPanel={openPreviewPanelWithModifiedURL}
			product={product}
			isAdminHubAIEnabled={data?.isAIEnabled ?? false}
			rovoOptions={rovoOptions}
			{...atlassianIntelligenceProps}
			xpcProduct="jira"
			xpcSubProduct={product}
		>
			{children}
		</SmartCardProvider>
	);
};

export const LinkingProviders: React.ComponentType<{ children: React.ReactNode }> =
	componentWithCondition(
		isNativePreviewPanelOnWorkItemViewEnabled,
		LinkingProvidersNew,
		LinkingProvidersOld,
	);

const getProductForSmartCardProvider = (
	project: ProjectContext | null | undefined,
): ProductType | undefined => {
	switch (project?.projectType) {
		case 'software':
			return 'JSW';
		case 'business':
			return 'JWM';
		case 'service_desk':
			return 'JSM';
		case 'product_discovery':
			return 'JPD';
		default:
			return undefined;
	}
};
